// =============================================
// autoGenerateMapRunner.ts — orchestrator pelnego pipeline'u dla JEDNEJ mapy
// =============================================
// STOP-D planu auto-generate-maps. Wywolywany sekwencyjnie przez
// autoGenerateBulkRunner (STOP-E) dla kazdej wybranej mapy w partii.
//
// Sekwencja per mapa:
//   1. Atomic claim lock (UPDATE WHERE is_generating=false)
//   2. Pre-stage SBD (jesli boundaries_* puste -> buildBoundaries)
//   3. Per-stage loop: Seeds -> Nodes -> SERP -> Entities -> EAV -> Relations
//      Kazdy stage:
//        a) check stop/pause refs
//        b) auto-accept draftow (skip LLM zero kosztu)
//        c) skip-done check
//        d) wywolaj core LLM function + DB persist
//        e) auto-accept po INSERT (Seeds/Nodes drafts -> active/planned)
//   4. Success -> UPDATE last_generation_status='completed'
//   5. Fail (fail-fast, ZERO auto-retry) -> UPDATE last_generation_status='failed',
//      last_generation_error
//   6. Finally -> release lock (ZAWSZE, nawet na error)
//
// PARITY: prompty z DB (loadPrompt), pipeline_config z DB (loadStageConfig),
// modele z prompt.model, generowanie przez mapGenerationCore.* — bez fallbacku.
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { loadPrompt } from "@/services/llm/promptLoader";
import {
  generateSeedsAI,
  generateNodesBatchAI,
  extractEntitiesAI,
  extractEAVAI,
  generateRelationsAI,
  type PromptInput,
  type StageConfigInput,
} from "@/services/mapGenerationCore";
import { lightPass } from "@/services/validation/reverseSerpValidator";
import { saveSerpResultsToDb } from "@/services/validation/serpResultsSaver";
import { insertEntitiesAndLink } from "@/services/entityLinkingService";
import { buildBoundaries } from "@/services/validation/boundaryEnforcer";
import {
  normalizeToTokens,
  inferQueryFrameFromQuestion,
} from "@/utils/textNormalization";
import { hashQuestion } from "@/services/deduplicationService";
import { slugify } from "@/lib/slugify";
import {
  logPublic,
  logDebug,
  logError,
} from "@/services/contentEngine/contentLogger";
import type {
  ThematicMap,
  ThematicSeed,
  ThematicNode,
  ThematicEntity,
  ThematicEntityAttribute,
  ThematicNodeRelation,
  PromptStep,
  PromptModel,
  LLMEntityOutput,
  LLMEAVOutput,
  LLMRelationOutput,
} from "@/types/thematicMaps";

// =============================================
// Types
// =============================================

export type TargetStage =
  | "seeds"
  | "nodes"
  | "serp"
  | "entities"
  | "eav"
  | "relations";

const STAGE_ORDER: TargetStage[] = [
  "seeds",
  "nodes",
  "serp",
  "entities",
  "eav",
  "relations",
];

export interface ProgressEvent {
  stage: TargetStage | "sbd";
  type:
    | "stage_start"
    | "stage_progress"
    | "stage_done"
    | "stage_skipped"
    | "stage_failed";
  msg?: string;
  costUsd?: number;
  durationMs?: number;
  error?: string;
}

export interface RunMapAutoGenerateOptions {
  mapId: string;
  userId: string;
  targetStage: TargetStage;
  skipDone: boolean;
  withSerpValidation: boolean;
  isPausedRef?: { current: boolean };
  isStopRequestedRef?: { current: boolean };
  onProgress?: (event: ProgressEvent) => void;
}

export interface RunMapResult {
  success: boolean;
  completedStages: string[];
  errorStage?: string;
  error?: string;
  costUsd: number;
  /** True gdy lock juz byl zajety przez innego runnera. */
  alreadyLocked?: boolean;
}

// =============================================
// Cost heuristics (matches logRun w useMapGenerator)
//   $0.003 / 1k input tokens, $0.015 / 1k output tokens
// =============================================
function estimateCost(tokensIn: number, tokensOut: number): number {
  return (tokensIn * 0.003 + tokensOut * 0.015) / 1000;
}

// =============================================
// Pure DB helpers (bulk runner version, bez React hook)
// =============================================

const DEFAULT_PIPELINE_CONFIGS: Record<
  string,
  { batch_size: number; parallel_batches: number }
> = {
  entity_extraction: { batch_size: 50, parallel_batches: 3 },
  eav_extraction: { batch_size: 20, parallel_batches: 3 },
  linking: { batch_size: 30, parallel_batches: 3 },
  content_engine: { batch_size: 1, parallel_batches: 3 },
  seed_generation: { batch_size: 8, parallel_batches: 1 },
  node_generation: { batch_size: 20, parallel_batches: 1 },
};

async function loadActivePromptAsInput(
  step: PromptStep,
): Promise<PromptInput | null> {
  const loaded = await loadPrompt(step);
  if (!loaded) return null;
  return {
    prompt_template: loaded.template,
    model: loaded.model as PromptModel,
  };
}

async function loadStageConfig(
  step: string,
  userId: string,
): Promise<StageConfigInput> {
  try {
    const { data, error } = await (supabase as any)
      .from("pipeline_config")
      .select("batch_size, parallel_batches")
      .eq("user_id", userId)
      .eq("step", step)
      .maybeSingle();

    if (error || !data) {
      return (
        DEFAULT_PIPELINE_CONFIGS[step] || {
          batch_size: 50,
          parallel_batches: 3,
        }
      );
    }
    return {
      batch_size: Number(data.batch_size),
      parallel_batches: Number(data.parallel_batches),
    };
  } catch {
    return (
      DEFAULT_PIPELINE_CONFIGS[step] || { batch_size: 50, parallel_batches: 3 }
    );
  }
}

async function fetchMap(mapId: string): Promise<ThematicMap | null> {
  const { data, error } = await (supabase as any)
    .from("thematic_maps")
    .select("*")
    .eq("id", mapId)
    .maybeSingle();
  if (error || !data) return null;
  return data as ThematicMap;
}

async function fetchSeedsActive(mapId: string): Promise<ThematicSeed[]> {
  const { data } = await (supabase as any)
    .from("thematic_seeds")
    .select("*")
    .eq("map_id", mapId)
    .or("status.is.null,status.eq.active")
    .order("sort_order", { ascending: true });
  return (data || []) as ThematicSeed[];
}

async function fetchNodesAll(mapId: string): Promise<ThematicNode[]> {
  const { data } = await (supabase as any)
    .from("thematic_nodes")
    .select("*")
    .eq("map_id", mapId);
  return (data || []) as ThematicNode[];
}

async function fetchEntitiesAll(mapId: string): Promise<ThematicEntity[]> {
  const { data } = await (supabase as any)
    .from("thematic_entities")
    .select("*")
    .eq("map_id", mapId);
  return (data || []) as ThematicEntity[];
}

async function fetchAttributesAll(
  mapId: string,
): Promise<ThematicEntityAttribute[]> {
  const { data } = await (supabase as any)
    .from("thematic_entity_attributes")
    .select("*")
    .eq("map_id", mapId);
  return (data || []) as ThematicEntityAttribute[];
}

async function fetchRelationsAll(
  mapId: string,
): Promise<ThematicNodeRelation[]> {
  const { data } = await (supabase as any)
    .from("thematic_node_relations")
    .select("*")
    .eq("map_id", mapId);
  return (data || []) as ThematicNodeRelation[];
}

// =============================================
// Lock helpers
// =============================================

async function claimMapLock(
  mapId: string,
  jobId: string,
  initialStage: string,
): Promise<boolean> {
  const { data, error } = await (supabase as any)
    .from("thematic_maps")
    .update({
      is_generating: true,
      current_job_id: jobId,
      generation_started_at: new Date().toISOString(),
      generation_stage: initialStage,
    })
    .eq("id", mapId)
    .eq("is_generating", false)
    .select("id")
    .maybeSingle();

  if (error) {
    logError("AUTO_GEN", `Mapa ${mapId} — claim lock error: ${error.message}`);
    return false;
  }
  return !!data;
}

async function releaseMapLock(mapId: string): Promise<void> {
  try {
    await (supabase as any)
      .from("thematic_maps")
      .update({
        is_generating: false,
        current_job_id: null,
        generation_stage: null,
      })
      .eq("id", mapId);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "unknown";
    logError("AUTO_GEN", `Mapa ${mapId} — release lock error: ${msg}`);
  }
}

async function updateGenerationStage(
  mapId: string,
  stage: string,
): Promise<void> {
  await (supabase as any)
    .from("thematic_maps")
    .update({ generation_stage: stage })
    .eq("id", mapId);
}

async function markCompleted(mapId: string): Promise<void> {
  await (supabase as any)
    .from("thematic_maps")
    .update({
      last_generation_status: "completed",
      last_generation_error: null,
      last_generation_at: new Date().toISOString(),
    })
    .eq("id", mapId);
}

async function markFailed(
  mapId: string,
  errorStage: string,
  errorMsg: string,
): Promise<void> {
  await (supabase as any)
    .from("thematic_maps")
    .update({
      last_generation_status: "failed",
      last_generation_error: `Stage ${errorStage}: ${errorMsg.slice(0, 2000)}`,
      last_generation_at: new Date().toISOString(),
    })
    .eq("id", mapId);
}

// =============================================
// Stage detection helpers
// =============================================

async function isStageDone(
  mapId: string,
  stage: TargetStage,
): Promise<boolean> {
  if (stage === "seeds") {
    const { count } = await (supabase as any)
      .from("thematic_seeds")
      .select("id", { count: "exact", head: true })
      .eq("map_id", mapId)
      .or("status.is.null,status.eq.active");
    return (count || 0) > 0;
  }
  if (stage === "nodes") {
    const { count } = await (supabase as any)
      .from("thematic_nodes")
      .select("id", { count: "exact", head: true })
      .eq("map_id", mapId)
      .in("node_status", ["planned", "published", "refresh"]);
    return (count || 0) > 0;
  }
  if (stage === "serp") {
    const { count } = await (supabase as any)
      .from("thematic_nodes")
      .select("id", { count: "exact", head: true })
      .eq("map_id", mapId)
      .not("demand_validated_at", "is", null);
    return (count || 0) > 0;
  }
  if (stage === "entities") {
    const { count } = await (supabase as any)
      .from("thematic_entities")
      .select("id", { count: "exact", head: true })
      .eq("map_id", mapId);
    return (count || 0) > 0;
  }
  if (stage === "eav") {
    const { count } = await (supabase as any)
      .from("thematic_entity_attributes")
      .select("id", { count: "exact", head: true })
      .eq("map_id", mapId);
    return (count || 0) > 0;
  }
  if (stage === "relations") {
    const { count } = await (supabase as any)
      .from("thematic_node_relations")
      .select("id", { count: "exact", head: true })
      .eq("map_id", mapId);
    return (count || 0) > 0;
  }
  return false;
}

async function autoAcceptDrafts(
  mapId: string,
  stage: TargetStage,
): Promise<number> {
  if (stage === "seeds") {
    const { data } = await (supabase as any)
      .from("thematic_seeds")
      .update({ status: "active" })
      .eq("map_id", mapId)
      .eq("status", "draft")
      .select("id");
    return (data || []).length;
  }
  if (stage === "nodes") {
    const { data } = await (supabase as any)
      .from("thematic_nodes")
      .update({ node_status: "planned" })
      .eq("map_id", mapId)
      .eq("node_status", "draft")
      .select("id");
    return (data || []).length;
  }
  return 0;
}

// =============================================
// Pure bulk INSERT helpers (without React hooks)
// =============================================

async function insertSeedsBulkRaw(
  userId: string,
  mapId: string,
  llmSeeds: Array<{
    name: string;
    slug: string;
    intent_family: string[];
    priority: number;
    seed_type?: string;
    scope_coverage?: string[];
    [k: string]: unknown;
  }>,
  existingSeeds: ThematicSeed[],
): Promise<{ inserted: number; renamed: number }> {
  if (llmSeeds.length === 0) return { inserted: 0, renamed: 0 };

  const takenSlugs = new Set(existingSeeds.map((s) => s.slug).filter(Boolean));
  let renamed = 0;
  const rows = llmSeeds.map((s, i) => {
    // FIX (2026-06-09): slug ASCII (WP-zgodny). LLM zwraca slug z polskich
    // znaków ("łąka") → slugify do "laka" PRZED collision check (DB ma unique
    // index uq_seed_slug_per_map). Base do suffix -2/-3 to slug PO slugify.
    let slug = s.slug ? slugify(s.slug) : s.slug;
    if (slug && takenSlugs.has(slug)) {
      const base = slug;
      let n = 2;
      while (takenSlugs.has(`${base}-${n}`)) n++;
      slug = `${base}-${n}`;
      renamed++;
    }
    if (slug) takenSlugs.add(slug);
    return {
      user_id: userId,
      map_id: mapId,
      sort_order: existingSeeds.length + i,
      status: "active", // auto-accept od razu (NIE 'draft')
      ...s,
      slug,
    };
  });

  const { data, error } = await (supabase as any)
    .from("thematic_seeds")
    .upsert(rows, {
      onConflict: "map_id,slug",
      ignoreDuplicates: true,
    })
    .select("id");

  if (error) {
    throw new Error(`insertSeedsBulkRaw: ${error.message}`);
  }
  return { inserted: (data || []).length, renamed };
}

async function insertNodesBulkRaw(
  userId: string,
  mapId: string,
  nodePreviewsBySeed: Array<{ seedId: string; nodes: Array<Record<string, unknown>> }>,
  existingNodes: ThematicNode[],
): Promise<{ inserted: number; renamed: number; skipped: number }> {
  // Flatten z seedId per node
  const allRows: Array<Record<string, unknown>> = [];
  for (const group of nodePreviewsBySeed) {
    for (const node of group.nodes) {
      // Skip duplicates (already flagged przez core)
      if (node.duplicate_level === "duplicate") continue;
      allRows.push({
        ...node,
        seed_id: group.seedId,
      });
    }
  }
  if (allRows.length === 0) return { inserted: 0, renamed: 0, skipped: 0 };

  const existingHashes = new Set(
    existingNodes.map((n) => n.canonical_question_hash).filter(Boolean),
  );
  const takenSlugs = new Set(existingNodes.map((n) => n.slug).filter(Boolean));

  let renamed = 0;
  let skipped = 0;
  const rows: Array<Record<string, unknown>> = [];

  for (const p of allRows) {
    const hash = p.canonical_question_hash as string | undefined;
    if (hash && existingHashes.has(hash)) {
      skipped++;
      continue;
    }

    // FIX (2026-06-09): slug ASCII (WP-zgodny). LLM zwraca slug z polskimi
    // znakami ("łąka") → slugify do "laka" PRZED collision check (DB ma unique
    // index uq_node_slug_per_map). Base do suffix -2/-3 to slug PO slugify.
    const rawSlug = p.slug as string | undefined;
    let slug = rawSlug ? slugify(rawSlug) : rawSlug;
    if (slug && takenSlugs.has(slug)) {
      const base = slug;
      let n = 2;
      while (takenSlugs.has(`${base}-${n}`)) n++;
      slug = `${base}-${n}`;
      renamed++;
    }
    if (slug) takenSlugs.add(slug);

    const question =
      (p.canonical_question as string) ||
      (p.title as string) ||
      "";

    // Explicit whitelist kolumn z thematic_nodes — NIE spreaduj `...p` zeby uniknac
    // przekazania in-memory pol z NodePreview (duplicate_level, duplicate_reason,
    // duplicate_match_slug, primary_entity, required_entities — bez _raw suffix)
    // ktorych DB schema nie ma.
    rows.push({
      user_id: userId,
      map_id: mapId,
      seed_id: p.seed_id,
      sort_order: existingNodes.length + rows.length,
      node_status: "planned", // auto-accept od razu (NIE 'draft')
      title: p.title,
      slug,
      document_role: p.document_role,
      primary_intent: p.primary_intent,
      primary_entity_raw: p.primary_entity,
      required_entities_raw: p.required_entities || [],
      canonical_question: p.canonical_question,
      canonical_question_hash: hash,
      canonical_question_tokens_normalized: normalizeToTokens(question),
      query_frame: inferQueryFrameFromQuestion(question),
    });
  }

  if (rows.length === 0) return { inserted: 0, renamed, skipped };

  const { data, error } = await (supabase as any)
    .from("thematic_nodes")
    .upsert(rows, {
      onConflict: "map_id,canonical_question_hash",
      ignoreDuplicates: true,
    })
    .select("id");

  if (error) {
    throw new Error(`insertNodesBulkRaw: ${error.message}`);
  }
  return { inserted: (data || []).length, renamed, skipped };
}

async function insertEAVBulkRaw(
  userId: string,
  mapId: string,
  entities: ThematicEntity[],
  llmResults: LLMEAVOutput[],
  existingAttrs: ThematicEntityAttribute[],
): Promise<{ inserted: number; skippedEntity: number; skippedDupe: number }> {
  // Build entity name -> id map (canonical + aliases)
  const entityNameToId = new Map<string, string>();
  for (const entity of entities) {
    entityNameToId.set(entity.canonical_name.toLowerCase(), entity.id);
    for (const alias of entity.aliases || []) {
      if (!entityNameToId.has(alias.toLowerCase())) {
        entityNameToId.set(alias.toLowerCase(), entity.id);
      }
    }
  }

  const existingKeys = new Set(
    existingAttrs.map(
      (a) =>
        `${a.entity_id}|${a.attribute_name.toLowerCase()}|${a.value.toLowerCase()}`,
    ),
  );

  const rows: Array<Record<string, unknown>> = [];
  let skippedEntity = 0;
  let skippedDupe = 0;

  for (const entityResult of llmResults) {
    const entityId = entityNameToId.get(
      entityResult.entity_canonical_name.toLowerCase(),
    );
    if (!entityId) {
      skippedEntity++;
      continue;
    }

    for (const attr of entityResult.attributes) {
      const key = `${entityId}|${attr.attribute_name.toLowerCase()}|${attr.value.toLowerCase()}`;
      if (existingKeys.has(key)) {
        skippedDupe++;
        continue;
      }
      rows.push({
        user_id: userId,
        map_id: mapId,
        entity_id: entityId,
        attribute_name: attr.attribute_name.toLowerCase(),
        value: attr.value,
        value_type: attr.value_type,
        source: "ai",
        confidence: Math.max(0, Math.min(1, attr.confidence)),
        notes: "",
        sort_order: 0,
      });
      existingKeys.add(key);
    }
  }

  if (rows.length === 0) return { inserted: 0, skippedEntity, skippedDupe };

  const { data, error } = await (supabase as any)
    .from("thematic_entity_attributes")
    .insert(rows)
    .select("id");

  if (error) {
    throw new Error(`insertEAVBulkRaw: ${error.message}`);
  }
  return { inserted: (data || []).length, skippedEntity, skippedDupe };
}

async function insertRelationsBulkRaw(
  userId: string,
  mapId: string,
  nodes: ThematicNode[],
  llmResults: LLMRelationOutput[],
  existingRels: ThematicNodeRelation[],
): Promise<{
  inserted: number;
  skippedSlug: number;
  skippedSelf: number;
  skippedDupe: number;
}> {
  const slugToNodeId = new Map(nodes.map((n) => [n.slug, n.id]));
  const existingRelKeys = new Set(
    existingRels.map(
      (r) => `${r.from_node_id}|${r.to_node_id}|${r.relation_type}`,
    ),
  );

  const rows: Array<Record<string, unknown>> = [];
  let skippedSlug = 0;
  let skippedSelf = 0;
  let skippedDupe = 0;

  for (const rel of llmResults) {
    const fromSlug = rel.from_slug.replace(/^\//, "").trim();
    const toSlug = rel.to_slug.replace(/^\//, "").trim();
    const fromId = slugToNodeId.get(fromSlug);
    const toId = slugToNodeId.get(toSlug);

    if (!fromId || !toId) {
      skippedSlug++;
      continue;
    }
    if (fromId === toId) {
      skippedSelf++;
      continue;
    }
    const key = `${fromId}|${toId}|${rel.relation_type}`;
    if (existingRelKeys.has(key)) {
      skippedDupe++;
      continue;
    }

    rows.push({
      user_id: userId,
      map_id: mapId,
      from_node_id: fromId,
      to_node_id: toId,
      relation_type: rel.relation_type,
      weight: Math.max(0, Math.min(10, Math.round(rel.weight))),
    });
    existingRelKeys.add(key);
  }

  if (rows.length === 0)
    return { inserted: 0, skippedSlug, skippedSelf, skippedDupe };

  const { data, error } = await (supabase as any)
    .from("thematic_node_relations")
    .insert(rows)
    .select("id");

  if (error) {
    throw new Error(`insertRelationsBulkRaw: ${error.message}`);
  }
  return {
    inserted: (data || []).length,
    skippedSlug,
    skippedSelf,
    skippedDupe,
  };
}

// =============================================
// Pause / stop check helper
// =============================================

async function checkPauseStop(
  isPausedRef?: { current: boolean },
  isStopRequestedRef?: { current: boolean },
): Promise<boolean> {
  if (isStopRequestedRef?.current) return true;
  while (isPausedRef?.current && !isStopRequestedRef?.current) {
    await new Promise((r) => setTimeout(r, 1000));
  }
  if (isStopRequestedRef?.current) return true;
  return false;
}

// =============================================
// Main orchestrator
// =============================================

export async function runMapAutoGenerate(
  opts: RunMapAutoGenerateOptions,
): Promise<RunMapResult> {
  const jobId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `job-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const completedStages: string[] = [];
  let totalCost = 0;
  let errorStage: string | undefined;
  let errorMsg: string | undefined;

  // ============ 1. Atomic claim lock ============
  const claimed = await claimMapLock(opts.mapId, jobId, "sbd");
  if (!claimed) {
    logError(
      "AUTO_GEN",
      `Mapa ${opts.mapId} — MAP_ALREADY_LOCKED (inny runner trzyma lock)`,
    );
    return {
      success: false,
      completedStages: [],
      error: "MAP_ALREADY_LOCKED",
      costUsd: 0,
      alreadyLocked: true,
    };
  }

  try {
    // Fetch swiezy stan mapy (po claim).
    let map = await fetchMap(opts.mapId);
    if (!map) {
      throw new Error("Mapa nie znaleziona po claim lock");
    }
    const mapName = map.name;
    const shortId = opts.mapId.slice(0, 8);
    logPublic(
      "AUTO_GEN",
      `Mapa "${mapName}" (id=${shortId}) — START job=${jobId}, target=${opts.targetStage}`,
    );

    // ============ 2. Pre-stage SBD (KOREKTA 6.2) ============
    const boundariesEmpty =
      (!map.boundaries_owned || map.boundaries_owned.length === 0) &&
      (!map.boundaries_not_owned || map.boundaries_not_owned.length === 0) &&
      (!map.boundaries_adjacent || map.boundaries_adjacent.length === 0);

    if (boundariesEmpty) {
      try {
        await updateGenerationStage(opts.mapId, "sbd");
        opts.onProgress?.({ stage: "sbd", type: "stage_start" });
        logPublic(
          "AUTO_GEN",
          `Mapa "${mapName}" — pre-stage SBD: boundaries puste -> generuje (deterministyczna funkcja, zero LLM cost)`,
        );
        const sbdStart = Date.now();
        const result = await buildBoundaries(opts.mapId);
        const sbdDur = Date.now() - sbdStart;
        logPublic(
          "AUTO_GEN",
          `Mapa "${mapName}" — DONE stage=sbd (owned=${result.owned.length}, not_owned=${result.not_owned.length}, adjacent=${result.adjacent.length}, ${(sbdDur / 1000).toFixed(1)}s)`,
        );
        opts.onProgress?.({
          stage: "sbd",
          type: "stage_done",
          costUsd: 0,
          durationMs: sbdDur,
        });
        // Refetch mapy zeby miec swiezy snapshot boundaries dla kolejnych stages.
        map = (await fetchMap(opts.mapId)) || map;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "unknown";
        errorStage = "sbd";
        errorMsg = msg;
        logError(
          "AUTO_GEN_ERR",
          `Mapa "${mapName}" — stage=sbd FAILED\n  Error: ${msg}\n  Stack: ${err instanceof Error ? err.stack?.slice(0, 500) : "n/a"}`,
        );
        opts.onProgress?.({
          stage: "sbd",
          type: "stage_failed",
          error: msg,
        });
        throw err;
      }
    } else {
      logPublic(
        "AUTO_GEN",
        `Mapa "${mapName}" — pre-stage SBD: OK (boundaries istnieja, owned=${map.boundaries_owned.length}, not_owned=${map.boundaries_not_owned.length}, adjacent=${map.boundaries_adjacent.length})`,
      );
    }

    // ============ 3. Per-stage loop ============
    const targetIdx = STAGE_ORDER.indexOf(opts.targetStage);
    const stagesToConsider = STAGE_ORDER.slice(0, targetIdx + 1);

    for (const stage of stagesToConsider) {
      // 3a. stop/pause check
      const shouldStop = await checkPauseStop(
        opts.isPausedRef,
        opts.isStopRequestedRef,
      );
      if (shouldStop) {
        logPublic(
          "AUTO_GEN",
          `Mapa "${mapName}" — stop requested, przerwam przed stage=${stage}`,
        );
        break;
      }

      // 3b. SERP toggle off — skip
      if (stage === "serp" && !opts.withSerpValidation) {
        logPublic(
          "AUTO_GEN",
          `Mapa "${mapName}" — stage=serp: toggle OFF, skip`,
        );
        completedStages.push(stage);
        opts.onProgress?.({
          stage,
          type: "stage_skipped",
          msg: "toggle off",
        });
        continue;
      }

      try {
        await updateGenerationStage(opts.mapId, stage);
        opts.onProgress?.({ stage, type: "stage_start" });
        const stageStart = Date.now();

        // 3c. Auto-accept draftow PRZED skip-done (KOREKTA 6.1)
        if (stage === "seeds" || stage === "nodes") {
          const acceptedCount = await autoAcceptDrafts(opts.mapId, stage);
          if (acceptedCount > 0) {
            logPublic(
              "AUTO_GEN",
              `Mapa "${mapName}" — stage=${stage}: znaleziono ${acceptedCount} draftow, auto-accept zamiast regeneracji (ZERO LLM cost)`,
            );
            completedStages.push(stage);
            opts.onProgress?.({
              stage,
              type: "stage_done",
              costUsd: 0,
              durationMs: Date.now() - stageStart,
              msg: `auto-accept ${acceptedCount} draftow`,
            });
            continue;
          }
        }

        // 3d. Skip-done check
        if (opts.skipDone && (await isStageDone(opts.mapId, stage))) {
          logPublic(
            "AUTO_GEN",
            `Mapa "${mapName}" — stage=${stage}: juz wykonany, skip`,
          );
          completedStages.push(stage);
          opts.onProgress?.({
            stage,
            type: "stage_skipped",
            msg: "already done",
          });
          continue;
        }

        // 3e. Execute stage
        logPublic(
          "AUTO_GEN",
          `Mapa "${mapName}" (id=${shortId}) — START stage=${stage}`,
        );

        const stageCost = await runStage(stage, map, opts);
        totalCost += stageCost;
        completedStages.push(stage);
        const stageDur = Date.now() - stageStart;
        logPublic(
          "AUTO_GEN",
          `Mapa "${mapName}" — DONE stage=${stage} (${(stageDur / 1000).toFixed(1)}s, $${stageCost.toFixed(4)})`,
        );
        opts.onProgress?.({
          stage,
          type: "stage_done",
          costUsd: stageCost,
          durationMs: stageDur,
        });
      } catch (stageErr) {
        const msg = stageErr instanceof Error ? stageErr.message : "unknown";
        errorStage = stage;
        errorMsg = msg;
        logError(
          "AUTO_GEN_ERR",
          `Mapa "${mapName}" — stage=${stage} FAILED\n  Error: ${msg}\n  Stack: ${stageErr instanceof Error ? stageErr.stack?.slice(0, 500) : "n/a"}\n  Params: target=${opts.targetStage}, skipDone=${opts.skipDone}, withSerp=${opts.withSerpValidation}`,
        );
        opts.onProgress?.({
          stage,
          type: "stage_failed",
          error: msg,
        });
        throw stageErr;
      }
    }

    // ============ 4. Success ============
    await markCompleted(opts.mapId);
    logPublic(
      "AUTO_GEN",
      `Mapa "${mapName}" — ALL DONE: ${completedStages.length}/${stagesToConsider.length} stages, $${totalCost.toFixed(4)} total`,
    );
    return {
      success: true,
      completedStages,
      costUsd: totalCost,
    };
  } catch (err) {
    // ============ 5. Fail (fail-fast, ZERO retry) ============
    const msg = err instanceof Error ? err.message : "unknown error";
    await markFailed(opts.mapId, errorStage || "unknown", errorMsg || msg);
    return {
      success: false,
      completedStages,
      errorStage,
      error: errorMsg || msg,
      costUsd: totalCost,
    };
  } finally {
    // ============ 6. Release lock ZAWSZE ============
    await releaseMapLock(opts.mapId);
  }
}

// =============================================
// Per-stage executors
// =============================================

async function runStage(
  stage: TargetStage,
  map: ThematicMap,
  opts: RunMapAutoGenerateOptions,
): Promise<number> {
  switch (stage) {
    case "seeds":
      return runStageSeeds(map, opts);
    case "nodes":
      return runStageNodes(map, opts);
    case "serp":
      return runStageSerp(map, opts);
    case "entities":
      return runStageEntities(map, opts);
    case "eav":
      return runStageEAV(map, opts);
    case "relations":
      return runStageRelations(map, opts);
  }
}

async function runStageSeeds(
  map: ThematicMap,
  opts: RunMapAutoGenerateOptions,
): Promise<number> {
  const prompt = await loadActivePromptAsInput("seed_generation");
  if (!prompt) throw new Error("PROMPT_NOT_FOUND: seed_generation");

  const coreResult = await generateSeedsAI({ map, prompt });
  if (!coreResult.success || !coreResult.data) {
    throw new Error(coreResult.error || "generateSeedsAI failed");
  }

  const existing = await fetchSeedsActive(opts.mapId);
  const { inserted } = await insertSeedsBulkRaw(
    opts.userId,
    opts.mapId,
    coreResult.data,
    existing,
  );
  logDebug(
    "AUTO_GEN",
    `Mapa "${map.name}" — insertSeedsBulk OK (${inserted} inserted, ${coreResult.data.length - inserted} duplikatow pominieto)`,
  );
  return estimateCost(coreResult.tokensInput, coreResult.tokensOutput);
}

async function runStageNodes(
  map: ThematicMap,
  opts: RunMapAutoGenerateOptions,
): Promise<number> {
  const nodePrompt = await loadActivePromptAsInput("node_generation");
  if (!nodePrompt) throw new Error("PROMPT_NOT_FOUND: node_generation");
  const dedupPrompt = await loadActivePromptAsInput("deduplication");
  const config = await loadStageConfig("node_generation", opts.userId);

  // Fetch swiezy stan: seeds active + nodes existing
  const seeds = await fetchSeedsActive(opts.mapId);
  if (seeds.length === 0) {
    throw new Error("Brak aktywnych seedow do generowania nodow");
  }
  const existingNodes = await fetchNodesAll(opts.mapId);
  const seedsWithoutNodes = seeds.filter(
    (s) => existingNodes.filter((n) => n.seed_id === s.id).length === 0,
  );

  if (seedsWithoutNodes.length === 0) {
    logDebug(
      "AUTO_GEN",
      `Mapa "${map.name}" — wszystkie seeds maja juz nodes, skip generowania`,
    );
    return 0;
  }

  const coreResult = await generateNodesBatchAI({
    map,
    seedList: seedsWithoutNodes,
    existingNodes,
    nodePrompt,
    dedupPrompt: dedupPrompt ?? undefined,
    config,
  });
  if (!coreResult.success || !coreResult.data) {
    throw new Error(coreResult.error || "generateNodesBatchAI failed");
  }

  // Build per-seed insert payload
  const nodePreviewsBySeed = coreResult.data
    .filter((r) => !r.error && r.nodes.length > 0)
    .map((r) => ({
      seedId: r.seedId,
      nodes: r.nodes.map((n) => ({
        title: n.title,
        slug: n.slug,
        document_role: n.document_role,
        primary_intent: n.primary_intent,
        primary_entity: n.primary_entity,
        canonical_question: n.canonical_question,
        canonical_question_hash: n.canonical_question_hash || hashQuestion(n.canonical_question),
        required_entities: n.required_entities,
        duplicate_level: n.duplicate_level,
      })),
    }));

  const { inserted } = await insertNodesBulkRaw(
    opts.userId,
    opts.mapId,
    nodePreviewsBySeed,
    existingNodes,
  );
  logDebug(
    "AUTO_GEN",
    `Mapa "${map.name}" — insertNodesBulk OK (${inserted} inserted)`,
  );

  return estimateCost(coreResult.tokensInput, coreResult.tokensOutput);
}

async function runStageSerp(
  map: ThematicMap,
  opts: RunMapAutoGenerateOptions,
): Promise<number> {
  const nodes = await fetchNodesAll(opts.mapId);
  const plannedNodes = nodes.filter(
    (n) => n.node_status === "planned" || n.node_status === "published",
  );
  if (plannedNodes.length === 0) {
    logDebug(
      "AUTO_GEN",
      `Mapa "${map.name}" — brak nodes do walidacji SERP, skip`,
    );
    return 0;
  }

  // lightPass to pure service (validated z reverseSerpValidator.ts)
  const candidates = plannedNodes.map((n) => ({
    title: n.title,
    slug: n.slug,
    document_role: n.document_role,
    primary_intent: n.primary_intent,
    primary_entity: n.primary_entity_raw,
    canonical_question: n.canonical_question,
    canonical_question_hash: n.canonical_question_hash,
    required_entities: n.required_entities_raw || [],
    duplicate_level: "unique" as const,
  }));

  const results = await lightPass(
    candidates,
    map.language || "pl",
    Number(map.location_code) || 2616,
  );

  const outcome = await saveSerpResultsToDb(opts.mapId, results, {
    nodes: plannedNodes.map((n) => ({
      id: n.id,
      title: n.title,
      canonical_question_hash: n.canonical_question_hash,
    })),
  });
  logDebug(
    "AUTO_GEN",
    `Mapa "${map.name}" — saveSerpResultsToDb OK (${outcome.updated} updated, ${outcome.failed} failed, ${outcome.skipped} skipped)`,
  );

  // lightPass NIE zwraca tokens cost (mix DataForSEO + LLM). Approximate $0.003 per node.
  return plannedNodes.length * 0.003;
}

async function runStageEntities(
  map: ThematicMap,
  opts: RunMapAutoGenerateOptions,
): Promise<number> {
  const prompt = await loadActivePromptAsInput("entity_extraction");
  if (!prompt) throw new Error("PROMPT_NOT_FOUND: entity_extraction");
  const config = await loadStageConfig("entity_extraction", opts.userId);

  const nodes = await fetchNodesAll(opts.mapId);
  if (nodes.length === 0) throw new Error("Brak nodes do entity extraction");

  const coreResult = await extractEntitiesAI({
    map,
    nodes,
    prompt,
    config,
  });
  if (!coreResult.success || !coreResult.data) {
    throw new Error(coreResult.error || "extractEntitiesAI failed");
  }

  const { insertOutcome, linkOutcome } = await insertEntitiesAndLink({
    userId: opts.userId,
    mapId: opts.mapId,
    entities: coreResult.data as LLMEntityOutput[],
    nodes,
  });
  logDebug(
    "AUTO_GEN",
    `Mapa "${map.name}" — insertEntitiesAndLink OK (${insertOutcome.inserted.length} entities total, ${insertOutcome.duplicatesSkipped} duplikatow, ${linkOutcome.linked} linked, ${linkOutcome.unmatched.length} unmatched)`,
  );

  return estimateCost(coreResult.tokensInput, coreResult.tokensOutput);
}

async function runStageEAV(
  map: ThematicMap,
  opts: RunMapAutoGenerateOptions,
): Promise<number> {
  const prompt = await loadActivePromptAsInput("eav_extraction");
  if (!prompt) throw new Error("PROMPT_NOT_FOUND: eav_extraction");
  const config = await loadStageConfig("eav_extraction", opts.userId);

  const entities = await fetchEntitiesAll(opts.mapId);
  if (entities.length === 0) throw new Error("Brak encji do EAV extraction");
  const nodes = await fetchNodesAll(opts.mapId);
  const existingAttrs = await fetchAttributesAll(opts.mapId);

  const coreResult = await extractEAVAI({
    map,
    entities,
    nodes,
    prompt,
    config,
  });
  if (!coreResult.success || !coreResult.data) {
    throw new Error(coreResult.error || "extractEAVAI failed");
  }

  const { inserted, skippedEntity, skippedDupe } = await insertEAVBulkRaw(
    opts.userId,
    opts.mapId,
    entities,
    coreResult.data,
    existingAttrs,
  );
  logDebug(
    "AUTO_GEN",
    `Mapa "${map.name}" — insertEAVBulk OK (${inserted} attrs, ${skippedEntity} unknown entity, ${skippedDupe} dupes)`,
  );

  return estimateCost(coreResult.tokensInput, coreResult.tokensOutput);
}

async function runStageRelations(
  map: ThematicMap,
  opts: RunMapAutoGenerateOptions,
): Promise<number> {
  const prompt = await loadActivePromptAsInput("linking");
  if (!prompt) throw new Error("PROMPT_NOT_FOUND: linking");
  const config = await loadStageConfig("linking", opts.userId);

  const nodes = await fetchNodesAll(opts.mapId);
  if (nodes.length === 0)
    throw new Error("Brak nodes do generowania relacji");
  const existingRels = await fetchRelationsAll(opts.mapId);

  const coreResult = await generateRelationsAI({
    map,
    nodes,
    prompt,
    config,
  });
  if (!coreResult.success || !coreResult.data) {
    throw new Error(coreResult.error || "generateRelationsAI failed");
  }

  const outcome = await insertRelationsBulkRaw(
    opts.userId,
    opts.mapId,
    nodes,
    coreResult.data,
    existingRels,
  );
  logDebug(
    "AUTO_GEN",
    `Mapa "${map.name}" — insertRelationsBulk OK (${outcome.inserted} rels, ${outcome.skippedSlug} unknown slugs, ${outcome.skippedSelf} self, ${outcome.skippedDupe} dupes)`,
  );

  return estimateCost(coreResult.tokensInput, coreResult.tokensOutput);
}
